import { css } from '@emotion/react';
import { Button, Page, Divider } from '@frontend/generic-dialpad-accessories';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { CallInformation } from '../components/call-information';
import { PageHeaderWraps } from '../components/generic/page-header-wraps';
import { HeldCall } from '../components/hold/held-call';
import { MergedCallGroupHeldCall } from '../components/hold/merged-call-group-held-call';
import { SettingsRow } from '../components/settings-row';
import { useSoftphoneCallState } from '../providers/softphone-call-state-provider';
import { useSoftphoneCallActions } from '../providers/softphone-call-state-provider/softphone-call-actions.provider';
import { useSoftphoneEventsEmitter } from '../providers/softphone-events-provider';
import { useSoftphoneRouter } from '../providers/softphone-router-provider';
import { isEstablishedCall, isOutgoingCall, isTerminatedCall } from '../types';

export const PageCallHomeMobile = () => {
  const { t } = useTranslation('softphone');
  const primaryCall = useSoftphoneCallState((ctx) => ctx.primaryCall);
  const establishedCalls = useSoftphoneCallState((ctx) => ctx.establishedCalls);
  const setCurrentRoute = useSoftphoneRouter((ctx) => ctx.setSingleCallRoute);
  const disabled = !primaryCall || !isEstablishedCall(primaryCall) || isTerminatedCall(primaryCall);
  const mergeCalls = useSoftphoneCallActions((ctx) => ctx.mergeCalls);
  const canMerge = establishedCalls.length > 1;
  const mergedCallIds = useSoftphoneCallState((ctx) => ctx.mergedCallIds);
  const mergedCallGroup = useSoftphoneCallState((ctx) => ctx.mergedCallGroup);
  const otherHeldCalls = establishedCalls.filter(
    (call) => !!call.onHoldSince && primaryCall?.id !== call.id && !mergedCallIds?.includes(call.id)
  );
  const { emit } = useSoftphoneEventsEmitter();
  const isMergedCallPrimaryCall = useSoftphoneCallState((ctx) => ctx.isMergedCallPrimaryCall);

  const handleTransfer = () => {
    setCurrentRoute('transfer');
    const call = establishedCalls.find((call) => call.id === primaryCall?.id);
    if (call) emit('transfer-call.initiated', { call });
  };
  const primaryCallStartTime =
    primaryCall && isOutgoingCall(primaryCall) ? primaryCall.placedAt : primaryCall?.establishedAt;

  const toggleMute = useSoftphoneCallActions((ctx) => ctx.toggleMute);
  const isMuted = useSoftphoneCallState((ctx) => ctx.isMuted);
  const connected = primaryCall && isEstablishedCall(primaryCall);
  const toggleHold = useSoftphoneCallActions((ctx) => ctx.toggleHold);
  const endCall = useSoftphoneCallActions((ctx) => ctx.endCall);
  if (!primaryCall) return null;

  return (
    <Page>
      <PageHeaderWraps hasBackButton={false}></PageHeaderWraps>
      <Page.Body
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          margin: ${theme.spacing(3, 0)};
          gap: ${theme.spacing(7)};
          flex: 1;
        `}
      >
        <CallInformation
          textAlign='center'
          connectedAt={primaryCallStartTime}
          displayName={primaryCall?.remoteParty.displayName}
          uri={primaryCall?.remoteParty.uri ?? ''}
          hideCallDuration={!connected}
        />
        <div
          css={css`
            display: grid;
            grid-template-columns: 1fr;
            grid-template-rows: repeat(auto-fill, minmax(90px, auto));
            gap: ${theme.spacing(2)};
            justify-content: center;
          `}
        >
          <div css={{ display: 'flex', gap: theme.spacing(3) }}>
            <Button.Round
              disabled={disabled}
              onClick={() => setCurrentRoute('park')}
              iconName={'phone-parked-calls'}
              label={t('Park')}
            ></Button.Round>
            <Button.Round
              trackingId='softphone-active-transfer'
              disabled={disabled}
              onClick={handleTransfer}
              iconName={'phone-outgoing'}
              label={t('Transfer')}
            ></Button.Round>
            {canMerge && (
              <Button.Round
                disabled={disabled}
                onClick={() => {
                  mergeCalls(establishedCalls);
                }}
                iconName={'merge'}
                label={t('Merge')}
              ></Button.Round>
            )}
            <Button.Round
              trackingId='softphone-active-keypad'
              disabled={disabled}
              onClick={() => setCurrentRoute('keypad')}
              iconName={'dial'}
              label={t('Keypad')}
            ></Button.Round>
          </div>
          <div css={{ display: 'flex', gap: theme.spacing(3), margin: 'auto' }}>
            <Button.Round
              trackingId='softphone-active-mute'
              onClick={(e) => {
                e.stopPropagation();
                toggleMute(!isMuted);
              }}
              selected={isMuted}
              disabled={!connected}
              iconName={isMuted ? 'mute' : 'microphone'}
              label={t('Mute')}
              color={isMuted ? 'inverted' : 'default'}
            ></Button.Round>

            <Button.Round
              trackingId='softphone-active-hold'
              disabled={!connected}
              selected={connected && !!primaryCall.onHoldSince}
              onClick={(e) => {
                e.stopPropagation();
                if (connected) {
                  toggleHold(primaryCall, !primaryCall.onHoldSince);
                }
              }}
              label={t('Hold')}
              iconName={'pause'}
              color={connected && !!primaryCall.onHoldSince ? 'inverted' : 'default'}
            ></Button.Round>

            <Button.Round
              trackingId='softphone-active-addcall'
              disabled={disabled}
              onClick={() => {
                setCurrentRoute('add-call');
              }}
              iconName={'plus'}
              label={t('Add Call')}
            ></Button.Round>
          </div>
          <Button.Round
            css={{ margin: '8px auto' }}
            trackingId='softphone-active-endcall'
            aria-label={isEstablishedCall(primaryCall) ? 'End Softphone Call' : 'End Softphone Ringing'}
            disabled={!(isEstablishedCall(primaryCall) || isOutgoingCall(primaryCall))}
            onClick={(e) => {
              e.stopPropagation();
              endCall(primaryCall);
            }}
            color='red'
            iconName={'phone-hangup'}
          ></Button.Round>
        </div>
      </Page.Body>
      <Page.Footer>
        {(!!otherHeldCalls.length || (!isMergedCallPrimaryCall && !!mergedCallGroup?.length)) && (
          <>
            <Divider />
            <Text
              color='white'
              css={css`
                margin-bottom: ${theme.spacing(1)};
              `}
            >
              {t('On Local Hold')}
            </Text>
            {!!mergedCallGroup?.length && <MergedCallGroupHeldCall />}
            {otherHeldCalls.map((call) => (
              <HeldCall key={call.id} call={call}></HeldCall>
            ))}
          </>
        )}
        <Divider />
        <SettingsRow />
      </Page.Footer>
    </Page>
  );
};
